<template>
  <div class="chatbot-container">
    <section v-if="isOpen" class="chatbot-window" aria-label="Fun識香港 AI 助手">
      <div class="chatbot-header">
        <div>
          <div class="chatbot-header__title"><span>🤖</span> Fun識香港 AI 助手</div>
          <div class="chatbot-header__quota">對話保留 365 日，逾期自動刪除</div>
        </div>
        <button class="chatbot-header__close" aria-label="關閉 AI 助手" @click="toggleChat">&times;</button>
      </div>

      <div class="chatbot-toolbar">
        <button :disabled="isBusy" :aria-expanded="showHistory" @click="toggleHistory">
          {{ showHistory ? '返回對話' : '歷史對話' }}
        </button>
        <span class="chatbot-toolbar__title" :title="conversationTitle">{{ conversationTitle || '新對話' }}</span>
        <button :disabled="isBusy" @click="startNewConversation">＋ 新對話</button>
      </div>

      <div v-if="showHistory" class="chatbot-history">
        <p class="chatbot-history__note">登入後可查看自己的歷史對話。</p>
        <div v-if="historyError" class="chatbot-notice" role="alert">
          <span>{{ historyError }}</span>
          <button :disabled="isLoadingHistory" @click="loadHistoryPage(historyRetryPage)">重新載入</button>
        </div>
        <p v-if="!isLoadingHistory && !historyError && !conversations.length" class="chatbot-status">暫時未有歷史對話。</p>
        <button
          v-for="conversation in conversations"
          :key="conversation.id"
          class="chatbot-history__item"
          :class="{ 'chatbot-history__item--active': conversation.id === conversationId }"
          :disabled="isBusy"
          @click="selectConversation(conversation.id)"
        >
          <span>{{ conversation.title || 'AI 對話' }}</span>
          <time :datetime="conversation.updated_at">{{ formatDate(conversation.updated_at) }}</time>
        </button>
        <p v-if="isLoadingHistory" class="chatbot-status" role="status">正在載入歷史對話…</p>
        <button v-else-if="nextHistoryPage" class="chatbot-more" @click="loadHistoryPage(nextHistoryPage)">載入更多對話</button>
      </div>

      <div v-else ref="messagesContainer" class="chatbot-messages" :aria-busy="isBusy">
        <div v-if="initializationError" class="chatbot-notice" role="alert">
          <span>{{ initializationError }}</span>
          <button @click="loadLatestConversation">重新載入</button>
        </div>
        <p v-if="isInitializing || isLoadingMessages" class="chatbot-status" role="status">正在載入對話…</p>
        <div v-if="conversationError" class="chatbot-notice" role="alert">
          <span>{{ conversationError }}</span>
          <button @click="selectConversation(conversationId)">重新載入</button>
        </div>
        <button
          v-if="nextBeforeId"
          class="chatbot-more"
          :disabled="isLoadingOlder || isBusy"
          @click="loadOlderMessages"
        >{{ isLoadingOlder ? '正在載入…' : '載入較早訊息' }}</button>
        <div v-if="olderError" class="chatbot-notice" role="alert">{{ olderError }}</div>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="chatbot-msg"
          :class="{
            'chatbot-msg--bot': msg.role === 'assistant',
            'chatbot-msg--user': msg.role === 'user',
            'chatbot-msg--error': msg.role === 'error'
          }"
        >
          <div class="chatbot-msg__content"><template v-for="(part, partIndex) in messageParts(msg.content)" :key="partIndex"><router-link v-if="part.type === 'place'" class="chatbot-link" :to="part.href">查看景點詳情</router-link><a v-else-if="part.type === 'url'" class="chatbot-link" :href="part.href" target="_blank" rel="noopener noreferrer">{{ part.text }}</a><template v-else>{{ part.text }}</template></template></div>
          <time v-if="msg.created_at" class="chatbot-msg__time" :datetime="msg.created_at">{{ formatDate(msg.created_at) }}</time>
        </div>
        <div v-if="isSending" class="chatbot-typing" role="status" aria-label="AI 正在回覆"><span></span><span></span><span></span></div>
        <button v-if="retryText && !isBusy" class="chatbot-more" @click="editFailedMessage">編輯問題再試</button>
      </div>

      <form v-if="!showHistory" class="chatbot-input" @submit.prevent="handleSend">
        <input
          ref="inputField"
          v-model="userInput"
          class="chatbot-input__field"
          aria-label="向 AI 助手提問"
          placeholder="例如：我想去中環玩…"
          maxlength="1000"
          :disabled="!canSend"
          @keydown.enter="preventCompositionSubmit"
        />
        <button class="chatbot-input__send" type="submit" aria-label="傳送訊息" :disabled="!canSend || !userInput.trim()">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </form>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getConversation, listConversations, sendMessage } from './chatService'

const greeting = () => ({
  id: 'greeting',
  role: 'assistant',
  content: '你好！我係 Fun識香港 AI 助手 🎒\n\n你可以問我關於香港景點嘅問題，例如：\n• 「我想去中環玩，有咩景點推薦？」\n• 「沙田區有咩歷史景點？」\n• 「幫我規劃尖沙咀半日遊路線」\n\n問推薦地點時我唔會硬加路線；你講明要一日遊／路線，我先會按同一區域幫你排。'
})

export default {
  name: 'AiChatbot',
  data () {
    return {
      isOpen: false,
      showHistory: false,
      userInput: '',
      messages: [],
      conversations: [],
      conversationId: null,
      conversationTitle: '',
      nextHistoryPage: null,
      nextBeforeId: null,
      isInitializing: false,
      isLoadingHistory: false,
      isLoadingMessages: false,
      isLoadingOlder: false,
      isSending: false,
      initializationError: '',
      historyError: '',
      conversationError: '',
      olderError: '',
      historyRetryPage: 1,
      retryText: '',
      accountVersion: 0,
      conversationVersion: 0,
      historyVersion: 0,
      localMessageId: 0,
      isDisposed: false
    }
  },
  computed: {
    ...mapGetters({ me: 'auth/me' }),
    userId () {
      return this.me && this.me.id ? String(this.me.id) : null
    },
    isBusy () {
      return this.isInitializing || this.isLoadingMessages || this.isSending
    },
    canSend () {
      return !!this.userId && !this.isBusy && !this.isLoadingOlder && !this.initializationError && !this.conversationError
    }
  },
  watch: {
    userId: {
      immediate: true,
      handler () {
        this.resetForUser()
        if (this.userId) this.loadLatestConversation()
      }
    }
  },
  mounted () {
    this.emitter.on('toggleChatbot', this.toggleChat)
  },
  beforeUnmount () {
    this.isDisposed = true
    this.accountVersion += 1
    this.emitter.off('toggleChatbot', this.toggleChat)
  },
  methods: {
    resetForUser () {
      this.accountVersion += 1
      this.conversationVersion += 1
      this.historyVersion += 1
      this.messages = [greeting()]
      this.conversations = []
      this.conversationId = null
      this.conversationTitle = ''
      this.nextHistoryPage = null
      this.nextBeforeId = null
      this.userInput = ''
      this.retryText = ''
      this.showHistory = false
      this.isInitializing = false
      this.isLoadingHistory = false
      this.isLoadingMessages = false
      this.isLoadingOlder = false
      this.isSending = false
      this.initializationError = ''
      this.historyError = ''
      this.conversationError = ''
      this.olderError = ''
    },
    requestContext () {
      return { accountVersion: this.accountVersion, userId: this.userId }
    },
    isCurrentRequest (context) {
      return !this.isDisposed && context.accountVersion === this.accountVersion && context.userId === this.userId
    },
    toggleChat () {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.scrollToBottom()
        this.focusInput()
      }
    },
    async loadLatestConversation () {
      if (!this.userId || this.isBusy) return
      const context = this.requestContext()
      this.isInitializing = true
      this.initializationError = ''
      const result = await listConversations()
      if (!this.isCurrentRequest(context)) return
      this.isInitializing = false
      if (!result.success) {
        this.initializationError = result.message
        return
      }
      this.conversations = result.conversations
      this.nextHistoryPage = result.nextPage
      if (this.conversations.length) await this.selectConversation(this.conversations[0].id)
      else this.startNewConversation()
    },
    toggleHistory () {
      if (this.isBusy) return
      this.showHistory = !this.showHistory
      if (this.showHistory) this.loadHistoryPage(1)
      else {
        this.scrollToBottom()
        this.focusInput()
      }
    },
    async loadHistoryPage (page) {
      if (!this.userId || this.isLoadingHistory) return
      const context = this.requestContext()
      const version = ++this.historyVersion
      this.historyRetryPage = page
      this.isLoadingHistory = true
      this.historyError = ''
      const result = await listConversations(page)
      if (!this.isCurrentRequest(context) || version !== this.historyVersion) return
      this.isLoadingHistory = false
      if (!result.success) {
        this.historyError = result.message
        return
      }
      const conversations = page === 1 ? [] : this.conversations
      this.conversations = this.uniqueMessages([...conversations, ...result.conversations])
      this.nextHistoryPage = result.nextPage
    },
    startNewConversation () {
      if (this.isBusy) return
      this.conversationVersion += 1
      this.conversationId = null
      this.conversationTitle = ''
      this.messages = [greeting()]
      this.nextBeforeId = null
      this.userInput = ''
      this.retryText = ''
      this.initializationError = ''
      this.conversationError = ''
      this.olderError = ''
      this.isLoadingOlder = false
      this.showHistory = false
      this.scrollToBottom()
      this.focusInput()
    },
    async selectConversation (id) {
      if (!id || this.isBusy) return
      const context = this.requestContext()
      const version = ++this.conversationVersion
      const conversation = this.conversations.find(item => item.id === id)
      this.conversationId = id
      this.conversationTitle = conversation ? conversation.title : 'AI 對話'
      this.messages = []
      this.userInput = ''
      this.retryText = ''
      this.nextBeforeId = null
      this.showHistory = false
      this.initializationError = ''
      this.conversationError = ''
      this.olderError = ''
      this.isLoadingOlder = false
      this.isLoadingMessages = true
      const result = await getConversation(id)
      if (!this.isCurrentRequest(context) || version !== this.conversationVersion) return
      this.isLoadingMessages = false
      if (!result.success) {
        this.conversationError = result.message
        return
      }
      this.messages = result.messages
      this.conversationTitle = result.conversation.title
      this.nextBeforeId = result.nextBeforeId
      this.scrollToBottom()
      this.focusInput()
    },
    async loadOlderMessages () {
      if (!this.nextBeforeId || this.isBusy || this.isLoadingOlder) return
      const context = this.requestContext()
      const version = this.conversationVersion
      this.isLoadingOlder = true
      this.olderError = ''
      const result = await getConversation(this.conversationId, this.nextBeforeId)
      if (!this.isCurrentRequest(context) || version !== this.conversationVersion) return
      const element = this.$refs.messagesContainer
      const height = element ? element.scrollHeight : 0
      const top = element ? element.scrollTop : 0
      this.isLoadingOlder = false
      if (!result.success) {
        this.olderError = result.message
        return
      }
      this.messages = this.uniqueMessages([...result.messages, ...this.messages])
      this.nextBeforeId = result.nextBeforeId
      this.$nextTick(() => {
        if (this.isCurrentRequest(context) && version === this.conversationVersion && element && element === this.$refs.messagesContainer) {
          element.scrollTop = top + element.scrollHeight - height
        }
      })
    },
    async handleSend () {
      const text = this.userInput.trim()
      if (!text || !this.canSend) return
      const context = this.requestContext()
      const optimisticId = `local-${++this.localMessageId}`
      this.messages.push({ id: optimisticId, role: 'user', content: text })
      this.userInput = ''
      this.retryText = ''
      this.isSending = true
      this.scrollToBottom()
      const result = await sendMessage(text, this.conversationId)
      if (!this.isCurrentRequest(context)) return
      this.isSending = false
      if (result.conversationId) this.conversationId = result.conversationId
      if (result.messages.length) {
        this.messages = this.uniqueMessages([
          ...this.messages.filter(message => message.id !== optimisticId && message.id !== 'greeting'),
          ...result.messages
        ])
      } else if (result.success) {
        this.messages.push({ id: `local-${++this.localMessageId}`, role: 'assistant', content: result.message })
      }
      if (!result.success) {
        if (!result.messages.some(message => message.role === 'error')) {
          this.messages.push({ id: `local-${++this.localMessageId}`, role: 'error', content: result.message })
        }
        if (result.status === 404 && this.conversationId) this.conversationError = result.message
        this.retryText = text
      }
      if (result.conversationId) {
        if (!this.conversationTitle) this.conversationTitle = text.slice(0, 80)
        const previous = this.conversations.find(item => item.id === this.conversationId)
        const updated = {
          ...previous,
          id: this.conversationId,
          title: this.conversationTitle,
          updated_at: result.messages.length ? result.messages[result.messages.length - 1].created_at : new Date().toISOString()
        }
        this.conversations = [updated, ...this.conversations.filter(item => item.id !== updated.id)]
      }
      this.scrollToBottom()
      this.focusInput()
    },
    editFailedMessage () {
      this.userInput = this.retryText
      this.retryText = ''
      this.focusInput()
    },
    preventCompositionSubmit (event) {
      if (event.isComposing || event.keyCode === 229) event.preventDefault()
    },
    uniqueMessages (items) {
      return [...new Map(items.map(item => [item.id, item])).values()]
    },
    messageParts (text) {
      const parts = []
      const links = /https?:\/\/[^\s<>"']+|\/places\/\d+\b/g
      let start = 0
      let match
      while ((match = links.exec(text)) !== null) {
        if (match.index > start) parts.push({ type: 'text', text: text.slice(start, match.index) })
        const href = match[0]
        parts.push({ type: href.startsWith('/places/') ? 'place' : 'url', text: href, href })
        start = match.index + href.length
      }
      if (start < text.length) parts.push({ type: 'text', text: text.slice(start) })
      return parts
    },
    formatDate (value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString('zh-HK', {
        timeZone: 'Asia/Hong_Kong',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    },
    focusInput () {
      this.$nextTick(() => {
        if (this.isOpen && this.canSend && this.$refs.inputField) this.$refs.inputField.focus()
      })
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const element = this.$refs.messagesContainer
        if (element) element.scrollTop = element.scrollHeight
      })
    }
  }
}
</script>

<style lang="scss">
@import "style";
</style>
